import React, { type JSX } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { useAuthPrompt, type AuthPrompt } from '@/context/auth/AuthPromptProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useIsGDPRDevice, useIsUKGDPRDevice } from '@/hooks/user';
import genericMessages from '@/messages/messages';

import { Wrapper } from './styles';
import { AuthTermsProps } from './types';

const AuthTerms = ({ signupTermsText, loginTermsText, prefix, suffix }: AuthTermsProps) => {
	const { formatMessage } = useIntl();
	const { authPrompt } = useAuthPrompt();
	const isUKGDPRDevice = useIsUKGDPRDevice();
	const isGDPRDevice = useIsGDPRDevice();
	const useShortTerms = isGDPRDevice || isUKGDPRDevice;
	const { logEvent } = useAnalytics();

	const defaultTermsMessages: {
		[mode in AuthPrompt]: {
			[isShort in 'short' | 'normal']: MessageDescriptor;
		};
	} = {
		signup: {
			normal: genericMessages.signupTerms,
			short: genericMessages.signupTermsShort,
		},
		login: {
			normal: genericMessages.loginTerms2,
			short: genericMessages.loginTerms2,
		},
	};

	const defaultTermsMessage = formatMessage(
		defaultTermsMessages[authPrompt][useShortTerms ? 'short' : 'normal'],
		{
			terms: (
				<a
					href="/terms"
					target="_blank"
					onClick={() =>
						logEvent({
							eventName: 'Login Form : Terms : Clicked',
							eventProps: {
								mode: authPrompt,
							},
						})
					}
				>
					{formatMessage(genericMessages.terms)}
				</a>
			),
			privacypolicylink: (...chunks): JSX.Element => (
				<a
					href="/privacy"
					target="_blank"
					onClick={() =>
						logEvent({
							eventName: 'Login Form : Privacy Policy : Clicked',
							eventProps: {
								mode: authPrompt,
							},
						})
					}
				>
					{chunks}
				</a>
			),
		},
	);
	const customTermsMessages = authPrompt === 'login' ? loginTermsText : signupTermsText;

	const termsMessage = customTermsMessages ?? defaultTermsMessage;

	return (
		<Wrapper className="loginTerms" data-testid="auth-terms-refresh">
			{prefix && <>{prefix} </>}
			{Array.isArray(termsMessage)
				? termsMessage.map((item, index) => <React.Fragment key={`terms-${index}`}>{item}</React.Fragment>)
				: termsMessage}
			{suffix && <> {suffix}</>}
		</Wrapper>
	);
};

export default AuthTerms;
